import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Avatar, Icon, message, Tag, Text, theme, Title } from 'components';
import { ContextualChatContext, RightSidePanelPageType } from 'contexts';
import { UserController } from 'api';
import { PopulatedUser } from 'api/UserController/getProfile';
import { Loading } from '@app/react-common';
import { formatPhoneNumber } from 'utils';
import { useStartChat } from 'hooks';

const SideContactDetailsPanel = () => {
  const {
    setRightSidePanelPage,
    setSelectedContactId,
    selectedContactId,
    client,
  } = useContext(ContextualChatContext);

  const { startChat } = useStartChat();

  const onChatWithIndividual = () => {
    if (!client.userID || !selectedContactId) {
      return;
    }

    startChat({
      userId: client.userID,
      selectedUsers: [{ id: selectedContactId, bbUserId: selectedContactId }],
    });
  };

  const [contactData, setContactData] = useState<PopulatedUser>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data: PopulatedUser = await UserController.getProfile(
          selectedContactId,
          { populateOrg: true },
        );
        setContactData(data);
      } catch (error) {
        message.error('Unable to load user');
        setRightSidePanelPage(RightSidePanelPageType.ChatDetails);
      }
      setLoading(false);
    };
    if (selectedContactId) fetchUserInfo();
  }, [selectedContactId, setRightSidePanelPage]);

  return (
    <StyledDetailsContainer>
      <StyledPrimaryHeader>
        <Icon
          type="antdArrowLeftOutlined"
          width={18}
          color="primary"
          onClick={() => {
            setRightSidePanelPage(RightSidePanelPageType.ChatDetails);
            setSelectedContactId('');
          }}
        />
        <Title level={3} style={{ margin: '25px 0px' }}>
          Contact Details
        </Title>
      </StyledPrimaryHeader>
      {loading && <Loading />}
      {!loading && (
        <>
          {/* Contact Details */}
          <StyledContactDetailsContainer>
            <Avatar user={contactData} width={122} />
            <Title level={2} hideMargin>
              {contactData.firstName} {contactData.lastName}
            </Title>
            {contactData.title && (
              <Tag color="default">{contactData.title}</Tag>
            )}
            {contactData.org.name && (
              <Text type="secondary">{contactData.org.name.toUpperCase()}</Text>
            )}
          </StyledContactDetailsContainer>
          {/* Contact Buttons */}
          <StyledButtonsContainer>
            {contactData.phone && (
              <Text>Cell: {formatPhoneNumber(contactData.phone)}</Text>
            )}
            {contactData.altPhone && (
              <Text>Alt Cell: {formatPhoneNumber(contactData.altPhone)}</Text>
            )}
            <StyledPillContainer>
              {contactData.email && (
                <StyledPilledButton href={`mailto:${contactData.email}`}>
                  <Icon type="antdMailOutlined" color="primary" />
                  Email
                </StyledPilledButton>
              )}
              <StyledPilledButton onClick={onChatWithIndividual}>
                <Icon type="antdMessageOutlined" color="primary" />
                Chat
              </StyledPilledButton>
            </StyledPillContainer>
          </StyledButtonsContainer>
        </>
      )}
    </StyledDetailsContainer>
  );
};

const StyledPillContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledDetailsContainer = styled.div`
  flex: 1;
  min-width: 300px;
  height: 100%;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  padding: 0px 16px;
  border-left: 1px solid #e3e3e3;
  animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  // Todo: Animating width might be better than position. Revisit later
  @keyframes slideInRight {
    from {
      transform: translateX(-5%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StyledPrimaryHeader = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 12px;
`;

const StyledContactDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
`;

const StyledButtonsContainer = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  flex-direction: column;
  align-items: center;
`;

const StyledPilledButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.getColor('primary')};
  background-color: ${theme.getColor('blue1')};
  border: none;
  border-radius: 16px;
  padding: 4px 16px;
  font-weight: 600;
`;

export default SideContactDetailsPanel;
